/**
 * Jerarquia de tradicion documental
 * Ing. Carlos F. Castaneda M.
 *
 * Para los tipos documentales FACTURA COMERCIAL (143), AIR WAYBILL (147),
 * BILL OF LADING (562) y CARTA PORTE (101), cuando varios cargues ACTIVOS
 * comparten CLIIDXXX + DOCTRANS (o CLIIDXXX + DOCNROXX) y difieren en su
 * tradicion documental, solo se muestra el cargue de mayor jerarquia:
 *   1. LIBERADO   2. ORIGINAL   3. COPIA
 * Los cargues de jerarquia menor quedan colgados de la tarjeta visible.
 */

/** Metadato que guarda la tradicion documental para cada tipo documental */
export const METADATO_TRADICION = {
  143: "TRADOCXX", // FACTURA COMERCIAL
  147: "TRADOCUX", // AIR WAYBILL
  562: "TRADOCUX", // BILL OF LADING (BL)
  101: "TRADOCUX", // CARTA PORTE
};

/** Orden jerarquico de la tradicion documental, de mayor a menor */
export const ORDEN_TRADICION = ["LIBERADO", "ORIGINAL", "COPIA"];

/**
 * Normaliza un valor de metadato a mayusculas sin acentos ni espacios sobrantes
 * @param {*} valor
 * @returns {string}
 */
const normalizar = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toUpperCase();

/**
 * Devuelve el nombre del metadato de tradicion que aplica al documento
 * @param {object} row
 * @returns {string|null}
 */
export const metadatoTradicion = (row) => METADATO_TRADICION[row?.TIPOIDXX] || null;

/**
 * Devuelve la tradicion documental normalizada del documento
 * @param {object} row
 * @returns {string} cadena vacia si el tipo no maneja jerarquia
 */
export const tradicionDe = (row) => {
  const metadato = metadatoTradicion(row);
  if (!metadato) return "";
  return normalizar(row?.JSONMETX?.[metadato]);
};

/**
 * Posicion del documento dentro del orden jerarquico
 * @param {object} row
 * @returns {number|null} null cuando la tradicion no es reconocida
 */
export const rangoTradicion = (row) => {
  const indice = ORDEN_TRADICION.indexOf(tradicionDe(row));
  return indice === -1 ? null : indice;
};

/**
 * Aplana un metadato que puede llegar como valor simple o como arreglo
 * @param {*} valor
 * @returns {string}
 */
const aplanar = (valor) => {
  if (Array.isArray(valor)) {
    return valor.map((item) => normalizar(item)).filter(Boolean).sort().join(",");
  }
  return normalizar(valor);
};

/**
 * Numero que identifica al documento de cara al usuario, para mostrarlo junto
 * al tipo documental en la cabecera del bloque padre:
 *   - FACTURA COMERCIAL -> numero de factura (FACOMERX)
 *   - documentos de transporte -> DOCTRANS, o NUMBLXXX en los BL sin migrar
 * Se conserva el valor original (sin normalizar) porque es para mostrar.
 * @param {object} row
 * @returns {string} cadena vacia si el documento no tiene ninguno de los dos
 */
export const numeroDocumentoDe = (row) => identificacionDocumento(row).valor;

/**
 * Igual que numeroDocumentoDe pero devolviendo tambien la clave del metadato,
 * para poder rotular el dato con su nombre real (el de GEST0004, que llega en
 * METAKEYS) en lugar de una etiqueta inventada.
 * @param {object} row
 * @returns {{clave: string|null, valor: string}}
 */
export const identificacionDocumento = (row) => {
  const meta = row?.JSONMETX || {};
  const claves = ["FACOMERX", "DOCTRANS", "NUMBLXXX"];
  const tieneValor = (v) =>
    Array.isArray(v)
      ? v.some((x) => String(x ?? "").trim() !== "")
      : String(v ?? "").trim() !== "";
  const clave = claves.find((k) => tieneValor(meta[k]));
  if (!clave) return { clave: null, valor: "" };
  const v = meta[clave];
  const valor = Array.isArray(v)
    ? v.map((x) => String(x).trim()).filter(Boolean).join(", ")
    : String(v).trim();
  return { clave, valor };
};

/**
 * Construye la clave de agrupacion del documento: tipo documental + cliente +
 * documento de transporte (DOCTRANS) o, en su defecto, numero de DO (DOCNROXX).
 *
 * NUMBLXXX se trata como el mismo dato que DOCTRANS: el comando
 * EspecialReconstruccionDoctrans del gestorapi migra NUMBLXXX a DOCTRANS dia a
 * dia, asi que los cargues viejos del BL todavia lo guardan en NUMBLXXX. Leer
 * ambos evita agrupar por DOCNROXX y ocultar dos documentos de transporte
 * distintos que solo comparten el DO.
 *
 * @param {object} row
 * @returns {string|null} null cuando faltan metadatos para agrupar
 */
export const claveTradicion = (row) => {
  if (!metadatoTradicion(row)) return null;
  const cliente = aplanar(row?.JSONMETX?.CLIIDXXX);
  if (!cliente) return null;
  const transporte = aplanar(row?.JSONMETX?.DOCTRANS) || aplanar(row?.JSONMETX?.NUMBLXXX);
  const referencia = transporte || aplanar(row?.JSONMETX?.DOCNROXX);
  if (!referencia) return null;
  return `${row.TIPOIDXX}|${cliente}|${transporte ? "TRANSP" : "DOCNROXX"}|${referencia}`;
};

/**
 * Agrupa los documentos por clave de tradicion y deja visible unicamente el de
 * mayor jerarquia de cada grupo.
 * @param {Array<object>} rows - documentos devueltos por la consulta
 * @returns {{visibles: Array<object>, versiones: Map<string, Array<object>>, remap: Map<string, string>}}
 *   visibles: documentos que se deben pintar
 *   versiones: IDARCHXX visible -> cargues de jerarquia menor que oculta
 *   remap: IDARCHXX oculto -> IDARCHXX visible que lo representa
 */
export const colapsarPorTradicion = (rows = []) => {
  const grupos = new Map();
  const visibles = [];
  const versiones = new Map();
  const remap = new Map();

  rows.forEach((row) => {
    const clave = claveTradicion(row);
    const rango = rangoTradicion(row);
    // Solo participan los cargues ACTIVOS con tradicion documental reconocida
    if (!clave || rango === null || row.REGESTXX !== "ACTIVO") {
      visibles.push(row);
      return;
    }
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push({ row, rango });
  });

  grupos.forEach((items) => {
    if (items.length === 1) {
      visibles.push(items[0].row);
      return;
    }
    items.sort(
      (a, b) => a.rango - b.rango || String(a.row.IDARCHXX).localeCompare(String(b.row.IDARCHXX))
    );
    const mejorRango = items[0].rango;
    const principales = items.filter((item) => item.rango === mejorRango);
    const menores = items.filter((item) => item.rango > mejorRango);
    principales.forEach((item) => visibles.push(item.row));
    // Sin tradiciones distintas no hay nada que colapsar
    if (menores.length === 0) return;
    const cabeza = String(principales[0].row.IDARCHXX);
    versiones.set(
      cabeza,
      menores.map((item) => item.row)
    );
    menores.forEach((item) => remap.set(String(item.row.IDARCHXX), cabeza));
  });

  // Se respeta el orden en que la consulta entrego los documentos
  const orden = new Map(rows.map((row, i) => [String(row.IDARCHXX), i]));
  visibles.sort((a, b) => orden.get(String(a.IDARCHXX)) - orden.get(String(b.IDARCHXX)));

  return { visibles, versiones, remap };
};
